import { Injectable, OnDestroy, Inject, Optional } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';

import {
  BehaviorSubject,
  EMPTY,
  firstValueFrom,
  forkJoin,
  Observable,
  of as observableOf,
  of,
  Subject,
  Subscription,
  throwError as observableThrowError,
} from 'rxjs';
import { filter, takeUntil, catchError, map, mergeMap, tap, take, distinctUntilChanged } from 'rxjs';

import StorageType from '../models-internal/enums/storage-type.enum';
import SigninData from '../models-internal/signin-data';
import { SignupViewModel } from '../models-internal/signup-view-model';
import AddEmailMailingListRequest from '../models/add-email-mailing-list-request';
import UserAccountCreateRequest from '../models/user-account-create-request';
import UserListingFavoriteViewModel from '../models/user-listing-favorite-view-model';
import UserProfileViewModel from '../models/user-profile-view-model';
import UserSetting from '../models/user-setting';
import AccountMiniViewModel from '../models/account-mini-view-model';
import AccountViewModel from '../models/account-view-model';
import DigitalSignatureViewModel from '../models/digital-signature-view-model';
import NonAdminAccountViewModel from '../models/non-admin-account-view-model';
import State from '../models/state';
import UserClaimViewModel from '../models/user-claim-view-model';
import UserCreditsViewModel from '../models/user-credits-view-model';
import UserProfile from '../models/user-profile';
import UserReferral from '../models/user-referral';
import UserSettings from '../models/user-settings';
import ValidationResultModel from '../models/validation-result-model';

import { AppSettingsService } from './app-settings.service';
import { AppStorageService } from './app-storage.service';
import { ArrayService } from './array.service';
import { BackgroundCheckService } from './background-check.service';
import { ClientStorageService } from './client-storage.service';
import { CreditService } from './credit.service';
import { DateService } from './date.service';
import { FriendbuyService } from './friendbuy.service';
import { HelpService } from './help.service';
import { PaymentEventService } from './payment-event.service';
import { ValueService } from './value.service';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest, EventMessage, EventType, EndSessionRequest, SilentRequest, SsoSilentRequest, AuthenticationResult, InteractionRequiredAuthError, PopupRequest } from '@azure/msal-browser';
import ImpersonateViewModel from '../models/impersonate-view-model';
import { SHARED_CORE_CONFIG, SharedCoreConfig } from '../config/shared-core-config';

const campaignTagType = 'CampaignTag'; // matches api claim name, also used as local store key. should probably move somewhere else along with 'Admin' etc.

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  
  private readonly _destroying$ = new Subject<void>();
  private readonly _activatableExternalOrgCodes = ['DWR'];

  public listingFavorites = new BehaviorSubject<UserListingFavoriteViewModel | null>(null);

  public userProfile = new BehaviorSubject<UserProfileViewModel | null>(null);

  public userSettings = new BehaviorSubject<UserSettings | null>(null);

  public externalOrganizationCodes = new BehaviorSubject<string[] | null>(null);

  public activeOrg = new BehaviorSubject<string | null>(null);

  public refreshSource = new Subject<boolean>();
  public refresh$ = this.refreshSource.asObservable();

  private isLoggedInSource = new Subject<boolean>();
  // distinctUntilChanged: updateAuthenticationState() re-emits on every inProgress$ === None,
  // so suppress consecutive duplicate states to prevent redundant onUserLoggedIn()/logout handling.
  public isLoggedIn$ = this.isLoggedInSource.asObservable().pipe(distinctUntilChanged());
  private _isLoggedIn: boolean = false;

  // Awaitable "silent-token / session check has settled" signal for route guards.
  // null = not yet resolved; guards await a non-null value so they don't reject a
  // valid session during the async silent-token step that runs when the app boots
  // in a new tab. Emits true once the silent token is confirmed (or the session is
  // already established), false when definitively logged out or the token can't be
  // acquired. Deliberately independent of the profile/settings/suspension refresh
  // so admin route access (id-token IsAdmin claim) doesn't hinge on those calls
  // succeeding. BehaviorSubject so late subscribers (guards) replay the settled value.
  private authResolvedSource = new BehaviorSubject<boolean | null>(null);
  public authResolved$ = this.authResolvedSource.asObservable();

  // Awaitable "API claims have loaded" signal. isSuperAdmin reads Role.SA from
  // clientStorage.claims (fetched from the API, not carried in the id token), so
  // SuperAdminGuard must wait on this rather than authResolved$. null = not yet
  // resolved; true once claims are loaded, false when logged out or the load fails.
  private claimsResolvedSource = new BehaviorSubject<boolean | null>(null);
  public claimsResolved$ = this.claimsResolvedSource.asObservable();

  public signInData = new Subject<SigninData>();

  private cachedUserSettings: UserSettings;
  
  private signupConfigurations = [
    { id: 'create-title-only', title: 'Create Free Account', description: null } as SignupViewModel,
    {
      id: 'create-title-description',
      title: 'Create Free Account',
      description:
        'Create a free account to lease properties and to see property details, including photos, activities, boundaries, pricing, calendar, rules, and amenities.',
    } as SignupViewModel,
    { id: 'listing-details-create', title: 'Create Free Account', description: null } as SignupViewModel,

    {
      id: 'lease-invitation-create',
      title: 'Create Free Account',
      description: 'Create an account to complete lease participant registration.',
      successActionType: 'none',
    } as SignupViewModel,
  ];

  private _availableReservationCredit = new BehaviorSubject<UserCreditsViewModel>(null);
  paymentServiceSubscription: Subscription;

  public get fullName(): string | null {
    if (!this.profile) {
      return null;
    }

    if (this.profile && this.profile.firstName && this.profile.lastName){
      return `${this.profile.firstName} ${this.profile.lastName}`.trim();
    }

    return null;
  }

  public get availableReservationCredit(): BehaviorSubject<UserCreditsViewModel> {
    return this._availableReservationCredit;
  }

  public facebookToken: string = "";

  public isSuspended: boolean = false;

  private _ssoAttempted = false; // Flag to prevent repeated SSO attempts

  constructor(
    private http: HttpClient,
    readonly clientStorage: ClientStorageService,
    readonly appSettingsService: AppSettingsService,
    readonly datePipe: DatePipe,
    private dateHelper: DateService,
    private creditService: CreditService,
    private friendBuyService: FriendbuyService,
    paymentEvents: PaymentEventService,
    private valueHelper: ValueService,
    private arrayHelper: ArrayService,
    private helpService: HelpService,
    private storage: AppStorageService,
    private backgroundCheckService: BackgroundCheckService,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Optional() @Inject(SHARED_CORE_CONFIG) private sharedCoreConfig?: SharedCoreConfig
  ) {
    
    // Initialize MSAL and handle authentication state
    this.initializeMsalAuthentication();

    
    this.paymentServiceSubscription = paymentEvents.paid
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        this.refreshUserCredits();
      });

    if (this.isLoggedIn) {
      if (this.clientStorage.claims) {
        const codes = this.arrayHelper.convertFromCSV(this.clientStorage.claims['ExternalOrgCodes']);
        this.externalOrganizationCodes.next(codes);
      }

      this.activeOrg.next(this.storage.getSessionItem('activeOrg'));
    }
  }

  private get authAuthority(): string {
    return this.sharedCoreConfig?.authAuthority || '';
  }

  private get authApiScopes(): string[] {
    return this.sharedCoreConfig?.authApiScopes || [];
  }

  /**
   * Called once, before the instance is destroyed.
   */
  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Initialize MSAL authentication and handle authentication state changes
   */
  private initializeMsalAuthentication(): void {
    // Handle redirect promise
    // 1) Handle the redirect result (if any). Emits once; may emit null first.
    this.authService.handleRedirectObservable()
      .pipe(takeUntil(this._destroying$))
      .subscribe((result: AuthenticationResult | null) => {
        if (result?.account) {
          this.authService.instance.setActiveAccount(result.account);
        }
      });

      // 2) When no interaction is in progress, attempt silent SSO.
    this.msalBroadcastService.inProgress$
      .pipe(
        takeUntil(this._destroying$),
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(async () => {
        if (!this.authService.instance.getActiveAccount() && !this._ssoAttempted) {
          this._ssoAttempted = true; // Prevent repeated attempts
          
          const request: SilentRequest = {
            scopes: ['openid', 'profile'],
            // Use the SAME B2C sign-in (or custom) policy authority you use for login
            authority: this.authAuthority,
            redirectUri: window.location.origin // or a dedicated /silent-redirect route
            // loginHint: 'user@example.com' // optional if you can infer it
          };

          try {
            const res = await this.authService.instance.ssoSilent(request);
            if (res.account) {
              this.authService.instance.setActiveAccount(res.account);
              // (optional) kick off token fetch for API scopes here
              // await this.msal.instance.acquireTokenSilent({ scopes: ['api://.../scope'] });
            }
          } catch {
            // No existing B2C session; remain logged-out until user clicks Sign in
          }
        }
      });
    
    // Enable account storage events for cross-tab login/logout
    this.authService.instance.enableAccountStorageEvents();

    // Subscribe to account added/removed events (cross-tab scenarios)
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => 
          msg.eventType === EventType.ACCOUNT_ADDED || 
          msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.updateAuthenticationState();
      });

    // Subscribe to interaction status changes - this is the key observable for MSAL
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.updateAuthenticationState();
      });
  }

  /**
   * Update authentication state based on current MSAL accounts
   */
  private updateAuthenticationState(): void {
    const accounts = this.authService.instance.getAllAccounts();
    const wasLoggedIn = this._isLoggedIn;
    
    if (accounts.length === 0) {
      // No cached account: the user is definitively logged out.
      this._isLoggedIn = false;
      this.isLoggedInSource.next(false);
      this.authResolvedSource.next(false);   // session check settled: logged out
      this.claimsResolvedSource.next(false); // no session => no claims

      if (wasLoggedIn) {
        console.log('User logged out');
        // Clear any cached data if needed

        // Handle cross-tab logout - redirect to home if needed
        if (window.location.pathname !== "/" && !window.location.pathname.startsWith("/home")) {
          window.location.pathname = "/";
        }
      }
    } else if (!wasLoggedIn) {
      // A cached account exists but we have NOT yet confirmed a live session.
      // A cached MSAL account can outlive its refresh token, so account presence
      // alone is not proof of authentication. Defer the "logged in" signal until
      // acquireTokenAndInitialize() acquires a valid token silently; it emits
      // next(true) on success or next(false) if silent renewal fails.
      console.log('Cached account found; confirming session via silent token');
      this.acquireTokenAndInitialize();
    } else {
      // Session was already confirmed on a previous emission; keep the state.
      // distinctUntilChanged on isLoggedIn$ dedupes this repeated true.
      this._isLoggedIn = true;
      this.isLoggedInSource.next(true);
      // Already fully resolved on a prior emission (claims loaded then); replay
      // both settled values for late subscribers.
      this.authResolvedSource.next(true);
      this.claimsResolvedSource.next(true);
    }
  }

  /**
   * Acquire token silently and initialize user data
   */
  private acquireTokenAndInitialize(): void {
    const request = {
      scopes: this.authApiScopes,
      account: this.authService.instance.getActiveAccount()
    };

    this.authService.acquireTokenSilent(request).subscribe({
      next: (tokenResponse) => {
        console.log('Token acquired successfully');

        // Silent renewal succeeded: the session is live, so it is now safe to
        // mark the user logged in and broadcast it. onUserLoggedIn() runs off
        // this emission, so gating it here prevents it firing for stale accounts.
        this._isLoggedIn = true;
        this.isLoggedInSource.next(true);

        // Session confirmed by a valid token. Settle the guard signal now — before
        // (and independent of) the profile/settings refresh below — so AdminGuard
        // resolves on token + id-token claim alone. claimsResolved$ is settled
        // separately by completeSignIn() once the API claims load.
        this.authResolvedSource.next(true);

        this.gotToken(tokenResponse.accessToken);

        // Load user settings and check for preferred activity
        this.getUserSettingsFromApi().pipe(
          catchError(() => of(null))
        ).subscribe((userSettings: UserSettings | null) => {
          if (userSettings) {
            let states = userSettings?.userPreferredStates ?? [];
            if (!states || states.length === 0) {
              //this.router.navigate(['/user/preferred-activity']);
            }
          }
        });
      },
      error: (error) => {
        console.error('Token acquisition failed:', error);
        // Silent renewal failed (e.g. refresh token expired): the cached account
        // is stale, so treat the user as logged out.
        this._isLoggedIn = false;
        this.isLoggedInSource.next(false);
        this.authResolvedSource.next(false);   // session check settled: not valid
        this.claimsResolvedSource.next(false); // no valid session => no claims
      }
    });
  }

  /**
   * Initiate login redirect
   */
  public loginRedirect(redirectRequest?: RedirectRequest): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1)
      )
      .subscribe(() => this.authService.loginRedirect(redirectRequest));
  }

  /**
   * Initiate logout redirect
   */
  public logoutRedirect(): void {
    
    let logoutRequest: EndSessionRequest  = {
      postLogoutRedirectUri: "/"
    };

    this.resetImpersonation().subscribe({
      next: () => {
        console.log("Reset impersonation successful");
        
      },
      error: (error) => {
        console.error("Reset impersonation failed:", error);
      },
      complete: () => {
        console.log("Reset impersonation completed");
        this.authService.logoutRedirect(logoutRequest);
      }
    });
  }
    
    

  checkAndSetActiveAccount(){
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  getClaimFromIdToken(key: string): string | undefined {
    let account = this.authService.instance.getAllAccounts()[0];
    if (!account) {
      return undefined;
    }

    const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
    if (!idTokenClaims || !(key in idTokenClaims)) {
      return undefined;
    }

    const claimValue = idTokenClaims[key];
    return typeof claimValue === "undefined" || claimValue === null
      ? undefined
      : claimValue.toString();
  }
  
  get userId() {
    if (!this.isLoggedIn) {
      return undefined;
    }
    if (this.isImpersonating) {
      let impersonatedUserAccountId = this.getClaimFromIdToken("ImpersonatedUserAccountId");
      return parseInt(impersonatedUserAccountId, undefined);
    } else {
      //return parseInt(this.getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'), undefined);
      return parseInt(this.getClaimFromIdToken("UserAccountId"), undefined);
    }
  }

  get isImpersonating(): boolean {
    // only need to check if logged in
    if (this.isLoggedIn) {
      let impersonatedUserAccountId = this.getClaimFromIdToken("ImpersonatedUserAccountId");
      if (typeof impersonatedUserAccountId === "undefined") {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }

  async impersonate(account: AccountViewModel): Promise<boolean> {
    await firstValueFrom(this.http.post<ImpersonateViewModel>("user/impersonate/" + account.id.toString(), null));

    const activeAccount = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0];
    if (!activeAccount) {
      console.warn('Impersonation finished, but no active MSAL account was found for token refresh.');
      return false;
    }

    // Include API scopes so the token endpoint exchange returns a fresh
    // access token in the same request, with enrichment claims applied.
    const ssoReq: SsoSilentRequest = {
      account: activeAccount,
      authority: this.authAuthority,
      scopes: ["openid", "profile", ...this.authApiScopes],
      loginHint: activeAccount.username,
    };

    try {
      const tokenResponse = await this.authService.instance.ssoSilent(ssoReq);
      console.log('Impersonation ssoSilent result:', {
        hasAccessToken: !!tokenResponse?.accessToken,
        scopes: tokenResponse?.scopes,
        fromCache: tokenResponse?.fromCache,
        expiresOn: tokenResponse?.expiresOn,
      });

      // Refresh claims and profile so the UI picks up the impersonated user's name immediately.
      await firstValueFrom(this.refresh());
      return true;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        // Firefox (and browsers with strict tracking protection) block the hidden
        // iframe that ssoSilent relies on. Fall back to a popup so the B2C session
        // cookie can be read in a first-party context.
        console.warn('Impersonation ssoSilent failed (likely Firefox ETP), falling back to popup.', e);

        const popupReq: PopupRequest = {
          account: activeAccount,
          authority: this.authAuthority,
          scopes: ["openid", "profile", ...this.authApiScopes],
          loginHint: activeAccount.username,
        };

        try {
          await this.authService.instance.acquireTokenPopup(popupReq);
          await firstValueFrom(this.refresh());
          return true;
        } catch (popupError) {
          console.error('Impersonation popup fallback failed.', popupError);
          return false;
        }
      }

      console.error('Impersonation ssoSilent failed.', e);
      return false;
    }
  }

  resetImpersonation() {
    return this.http.post("user/resetimpersonation", null);
  }

  get isAdmin() {
    // Admin status must reflect a confirmed live session, not merely a cached
    // MSAL account. getClaimFromIdToken() reads from the cached id token, which
    // survives token/session expiry; without this guard a returning admin's
    // stale claims report isAdmin=true while effectively logged out, causing
    // admin-only API calls (e.g. system/admin/notification) to fire and 401.
    if (!this.isLoggedIn) {
      return false;
    }

    const claimValue = this.getClaimFromIdToken("IsAdmin");

    if (!claimValue) {
      return false;
    }

    return isTrueString(claimValue);
  }

  /**
   * Determines if the current user is the listing owner
   * @param listing
   */
  public isLandowner(listing): boolean {
    if (!listing) {
      return false;
    }

    return listing.userAccountId === this.userId;
  }

  /**
   * Property indicating if the current user is an OA system admin / sys engineer.  This property will be used to show
   * developer/engineer specific items.
   */
  get isSysAdmin() {
    if (this.cachedUserSettings) {
      return this.cachedUserSettings.isSysAdmin;
    }

    return false;
  }

  hasReservationInvitation() {
    return this.http.get<boolean>('UserProfile/HasReservationInvitation');
  }

  /**
   * Gets the public-facing ip address
   */
  public getIpAddress() {
    const url = `${this.appSettingsService.outdoorAccessPiebaldApiUrl}/ip`;

    const requestOptions: Object = {
      /* other options here */
      responseType: 'text',
    };

    // NOTE: switched out jsonip.com with an OA endpoint because jsonip.com was getting blocked by ad-blockers
    return this.http.get<string>(url, requestOptions);
  }

  /**
   * Refreshes the Available User Credits.  Observers of getAvailableReservationCredit will
   * receive the updated data.
   */
  public refreshUserCredits() {
    
    if (!this.userId) {
      this._availableReservationCredit.next(null);
      return;
    }

    this.creditService.getUserCreditBalance(this.userId).subscribe(
      (data) => {
        this._availableReservationCredit.next(data);
      },
      (err) => {
        console.error(err);
      }
    );
  }

  /** this is the username/email address for the authenticated user */
  get userName(): string {
    // return this.getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress');
    return this.getClaimFromIdToken("email");
  }

  get hasCompletedProfile(): boolean {
    if (!this.isLoggedIn) {
      return false;
    }
    return isTrueString(this.getClaim('hasCompletedProfile'));
  }

  validateCompleteUserProfile(userId: number) {
    return this.http.get<ValidationResultModel>(`UserProfile/Validate/${userId}`);
  }

  get backgroundCheckStatus(): string {
    if (!this.isLoggedIn) {
      return 'none';
    }
    return this.getClaim('backgroundCheckStatus') || 'none';
  }

  get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }


  public isLoggedInChangeRaise(v: boolean) {

    this.isLoggedInSource.next(v);

    if (this.isAdmin) {
      this.helpService.disabled = true;
    }
  }

  refreshRaise() {
    // console.log('from './user.service': refreshRaise()');
    this.refreshSource.next(true);
  }

  get campaignTag() {
    return this.clientStorage.campaignTag;
  }
  set campaignTag(tag) {
    this.clientStorage.campaignTag = tag;
  }

  private _profile: UserProfile;
  get profile() {
    return this._profile;
  }

  /**
   * Gets new claims from api/User/Claims endpoint
   */
  public refreshClaims() {
    
    return this.http.get<UserClaimViewModel[]>('User/Claims').pipe(
      map((data: UserClaimViewModel[]) => {
        this.clientStorage.claims = Object.assign({}, ...data.map(({ resource, action }) => ({ [resource]: action })));

        // steal a copy of campaignTag if it comes through as a claim.
        const campaignTag = this.clientStorage.claims[campaignTagType];
        if (campaignTag !== undefined) {
          this.campaignTag = campaignTag;
        }

        this.refreshExternalOrganizationCodes(data);

        return data; // not using .do so map wont emit until claims are captured.
        // .do does not cause emit.
      })
    );
  }

  /**
   * Refreshes the externalOrganizationCodes Subject with the ExternalOrgCodes claim
   */
  private refreshExternalOrganizationCodes(data: UserClaimViewModel[]) {
    
    let currentActiveOrgValue;

    if (this.activeOrg) {
      if (this.activeOrg.value){
        currentActiveOrgValue = this.activeOrg.value;
      }
      else{
        currentActiveOrgValue = null;
      }
      
    }

    if (!data) {
      this.externalOrganizationCodes.next(null);
      return;
    }

    const externalCodes = data.find((x) => x.resource === 'ExternalOrgCodes');

    if (externalCodes) {
      const values = this.arrayHelper.convertFromCSV(externalCodes.action);

      this.externalOrganizationCodes.next(values);

      if (this.activeOrg) {
        if (!currentActiveOrgValue && values && values.length > 0) {
          this.setActiveOrg(values[0]);
        } else {
          this.setActiveOrg(currentActiveOrgValue);
        }
      }
    } else {
      this.externalOrganizationCodes.next(null);
    }
  }

  /**
   * Gets claims from either local client storage or by issuing an HTTP
   * GET request to the api/User/Claims endpoint.
   */
  private getClaims(): Observable<UserClaimViewModel[]> {
    
    if (!this.isLoggedIn) {
      this.clientStorage.claims = {};
      return new Observable((observer) => {
        observer.next([] as UserClaimViewModel[]);
        observer.complete();
      });
    }

    return this.refreshClaims();
  }


  /*
   * Refresh the current user's claims and profile information from the server
   */
  refresh(): Observable<boolean> {
    // console.log("from './user.service': Refresh()");
    // alert("from './user.service': Refresh()");

    if (!this.isLoggedIn) {
      this.clientStorage.claims = {};
      this._profile = undefined;
      return new Observable((observer) => {
        this.refreshRaise();
        observer.next(undefined);
        observer.complete();
      });
    }

    return new Observable((observer) => {
      const sources = [this.getClaims(), this.getProfile(), this.getUserSettings(), this.isUserSuspended()]
      forkJoin(sources).subscribe({
        next: () => {
          this.refreshRaise();
          observer.next(true);
          observer.complete();
        },
        error: (e) => {
          observer.error(e);
        },
      });
    });
  }

  getClaim(key: 'NameIdentifier'): number | undefined;
  getClaim(key: string): string | undefined;
  getClaim(key: string) {
    const claimValue = (this.clientStorage.claims && this.clientStorage.claims[key]) || undefined;
    switch (key) {
      case 'NameIdentifier':
        return !isNaN(Number(claimValue)) ? Number(claimValue) : undefined;
      default:
        return claimValue;
    }
  }

  /**
   * Checks to see if the user has the claim for a resource/action
   */
  public hasClaim(resource: string, action: string) {
    const claimValue = this.getClaim(resource);

    if (claimValue && claimValue === action) {
      return true;
    }

    return false;
  }

  public gotToken(token) {
    return this.completeSignIn(token).subscribe();
  }

  // Function to store a value in a cookie with a 1-hour expiration
  setCookie(name: string, value: string, hours: number): void {
    const date = new Date();
    date.setTime(date.getTime() + hours * 60 * 60 * 1000); // Set expiration time
    const expires = "expires=" + date.toUTCString();
    document.cookie = `${name}=${value}; ${expires}; path=/`; // Set the cookie
  }

  // Function to read a cookie by name
  getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
    return null;
  }

  public saveUserGeoLocation(){
    const userCity = this.getCookie("user_city");
    const userState = this.getCookie("user_state");

    if (userCity && userState){
      const userGeoLocation = {
        detectedCity: userCity,
        detectedState: userState
      }
      this.http.post('useraccount/saveUserGeoLocation', userGeoLocation).subscribe();
    }
  }

  /**
   * Fired when sign-in actually occurs.  This is not called when the user's
   * browser is already signed in
   */
  private completeSignIn(_token: string) {

    this.refreshClaims().subscribe({
      next: () => {
        // Claims (incl. Role.SA) are now in clientStorage — settle the signal
        // SuperAdminGuard awaits.
        this.claimsResolvedSource.next(true);

        // get the user's listing favorites
        this.refreshListingFavorites();

        //Save user's geo location
        this.saveUserGeoLocation();

        this.refreshUserCredits();
      },
      error: () => {
        // Claims couldn't load: super-admin status is indeterminate, so settle
        // false rather than leave SuperAdminGuard waiting forever.
        this.claimsResolvedSource.next(false);
      }
    });

    return new Observable((observer) => {
      this.refresh().subscribe({
        next: () => {
          this.isLoggedInChangeRaise(true);
          observer.next(0);
          observer.complete();
        },
        error: (e) => {
          this.isLoggedInChangeRaise(false);
          observer.error(e);
          observer.complete();
        }
      });
    });

    
  }

  getProfile(): Observable<UserProfileViewModel> {
    
    if (!this.isLoggedIn) {
      this._profile = undefined;

      return new Observable((observer) => {
        observer.next(undefined);
        observer.complete();
      });
    }

    return this.http.get<UserProfileViewModel>('UserProfile').pipe(
      tap((p) => {
        try {
          if (p.dateOfBirth !== undefined && p.dateOfBirth !== null) {
            p.dateOfBirth = new Date(p.dateOfBirth);
          }
        } catch (e) {}

        this._profile = p;
        
        // Make it known to all subscribers!
        this.userProfile.next(p);
      })
    );
  }

  public get guestListingDetailViewCount(): number {
    if (this.isLoggedIn) {
      return 0;
    }

    const x = localStorage.getItem('orchestraPotato');

    if (x) {
      return +x;
    }

    return 0;
  }

  public set guestListingDetailViewCount(v: number) {
    localStorage.setItem('orchestraPotato', v.toString());
  }

  /**
   * Gets User Referral Data associated w/ the User Account
   */
  getUserReferralData() {
    
    if (this.userId > 0) {
      // if data is already loaded, this may be new referral data.
      // send it, and let the API figure out what to do with it
      if (this.hasInitialQueryParams) {
        let referralData: UserReferral = null;

        if (this.friendBuyService.getReferralCode() || sessionStorage.getItem('initialUrl')) {
          referralData = {} as UserReferral;
          referralData.referralCode = this.friendBuyService.getReferralCode();
          referralData.referralUrl = sessionStorage.getItem('initialUrl');
        }

        referralData.userAccountId = this.userId;

        this.http.post<UserReferral>('User/referral', referralData).subscribe(
          () => {
          },
          (err) => {
            console.error(err);
          }
        );

        return;
      }

      this.http.get<UserReferral>(`User/referral/${this.userId}`).subscribe((referralData) => {
        if (referralData && referralData.referralUrl) {
          
          window.location.href = referralData.referralUrl;
        }
      });
    } else {
      console.warn('No User ID. No user referral data retrieved.');
    }
  }

  getUserReferral(): Observable<UserReferral> {
    return this.http.get<UserReferral>(`User/referral/${this.userId}`);
  }

  /**
   * Sets User Referral Data
   * @param userReferralData
   */
  public setUserReferralData(userReferralData: UserReferral) {
    
    if (this.userId != userReferralData.userAccountId) {
      throw new Error('User ID Mismatch.');
    }

    return this.http.post<UserReferral>('User/referral', userReferralData);
  }

  /**
   * Is the local storage initialQueryParams set w/ data?
   */
  public get hasInitialQueryParams(): boolean {
    const json = this.storage.getSessionItem('initialQueryParams');

    if (!json) return false;

    const jsonObject = JSON.parse(json);

    if (jsonObject['fbuy_ref_code']) {
      return true;
    }

    return false;
  }

  /**
   * Gets the State (admin bnd) for a user.
   */
  getState() {
    return this.http.get<State>('location/calculate/state');
  }

  getOAStaff() {
    return this.http.get<AccountMiniViewModel[]>('admin/accounts/OAStaff');
  }

  /**
   * Property flag for Super Admin role
   */
  public get isSuperAdmin(): boolean {
    return this.hasClaim('Role.SA', 'Is');
  }

  /**
   * Clear the cached user settings
   */
  public clearCachedUserSettings() {
    this.cachedUserSettings = null;
    this.userSettings.next(null);
  }

  /**
   * Gets user settings of the currently logged in users. If this.cachedUserSettings is present, that object will be returned.
   * Otherwise, this method makes the API call and sets the local cachedUserSettings field.
   */
  public getUserSettings() {
    if (!this.isLoggedIn) {
      // console.log('User not logged in.  Returning empty observable.');
      this.cachedUserSettings = null;
      return EMPTY;
    }

    if (this.userId <= 0) {
      console.log('User id zero.  Returning empty observable.');
      this.cachedUserSettings = null;
      return EMPTY;
    }

    if (this.cachedUserSettings) {
      return observableOf(this.cachedUserSettings);
    }

    return this.getUserSettingsFromApi();
  }

  /**
   * Gets a single user setting by setting name
   */
  public getUserSetting(name: string) {
    return this.http.get<UserSetting>(`UserSettings/${name}`);
  }

  /**
   * Gets the UserSettings for the currently logged in user from the WebAPI and sets the cachedUserSettings
   *
   */
  public getUserSettingsFromApi() {
    if (!this.isLoggedIn) {
      return of(null);
    }

    return this.http.get<UserSettings>('UserSettings').pipe(
      map((x) => {
        this.cachedUserSettings = x;

        // Broadcast to subscribers
        this.userSettings.next(x);

        return x;
      }),
      catchError((error) => {
        console.log('getUserSettings.error', error);
        return of(null);
      })
    );
  }

  public getStripeCustomerPortalUrl(retUrl: string) {
    return this.http.get<string>('UserSettings/stripeCustomerPortalUrl?retUrl=' + retUrl );
  }

  /**
   * And administrative call to obtain the user settings for a userId
   */
  public getAccountUserSettings(userId: number) {
    if (!this.isAdmin) {
      return of(null);
    }

    console.log('Start WebAPI Request for user settings...');
    return this.http.get<UserSettings>(`UserSettings/admin/${userId}`).pipe(
      map((x) => {
        console.log('WebAPI Request for user settings result: ', x);
        this.cachedUserSettings = x;

        // Broadcast to subscribers
        this.userSettings.next(x);

        return x;
      }),
      catchError((error) => {
        console.log('getUserSettings.error', error);
        return of(null);
      })
    );
  }

  /**
   * Clears the cached user settings and then retrieves fresh data.
   *
   */
  public refreshUserSettings() {
    this.clearCachedUserSettings();

    this.getUserSettingsFromApi().subscribe(
      (results) => {
        console.log('subscription results=', results);
      },
      (err) => {
        console.error(err);
      }
    );
  }

  /**
   * Checks to see if the user is suspended
   * @param userId
   */
  public isUserSuspended(userId?: number): Observable<boolean> {
    if (!userId) {
      userId = this.userId;
    }

    if (Number.isNaN(userId)) {
      return observableOf(false);
    } else {
      return this.http.get<boolean>(`user/sus/${userId}`).pipe(
        map((b) => {
          this.isSuspended = b;
          return b;
        }),
        catchError((error) => {
          console.log('getUserSettings.error', error);
          return observableOf(false);
        })
      );
    }
  }

  /**
   * Sets a user setting value
   * @param name
   * @param val
   */
  setUserSettingValue(name: string, val: any) {
    if (!this.isLoggedIn) return;

    let setting = {
      name: name,
      value: val.toString(),
      updatedOn: this.dateHelper.nowUTC,
      userAccountId: this.userId,
    } as UserSetting;

    return this.updateUserSetting(setting).pipe(
      map((x) => {
        x.createdOn = this.dateHelper.makeValid(x.createdOn);
        x.updatedOn = this.dateHelper.makeValid(x.updatedOn);
        return x;
      })
    );
  }

  /**
   * Update User Setting
   */
  public updateUserSetting(setting: UserSetting) {
    if (!this.isLoggedIn) return EMPTY;

    return this.http.put<UserSetting>('UserSettings', setting);
  }

  public removeUserSetting(userId: number, settingName: string) {
    return this.http.delete<UserSetting>(`UserSettings/${userId}/${settingName}`);
  }

  /**
   * Signs up a new user - wahoo!
   * @param emailAddress
   * @param password
   * @param zip
   * @param campaignTag
   * @param membershipReferral
   */
  public signup(
    emailAddress: string,
    password: string,
    zip: string,
    campaignTag: string,
    membershipReferral: UserReferral,
    userPreferredActivitiesSetting: UserSetting,
    userPreferredStatesSetting: UserSetting
  ) {
    
    let model = { emailAddress: emailAddress, password: password, zip: zip, campaignTag: campaignTag } as UserAccountCreateRequest;

    if (membershipReferral) {
      model.referralCode = membershipReferral.referralCode;
      model.referralUrl = membershipReferral.referralUrl;
    }

    model.userPreferredActivitiesSetting = userPreferredActivitiesSetting;
    model.userPreferredStatesSetting = userPreferredStatesSetting;

    return this.http
      .post<{ access_token: string }>('useraccount', model)
      .pipe(mergeMap(({ access_token }) => this.completeSignIn(access_token)));
  }

  validateEmail(email: string) {
    return this.postCore('AccountRegistration/ValidateEmail', { emailAddress: email });
  }

  validateSignupToken(token: string) {
    const data = { token };

    return withUnwrappingRethrow(this.http.post('AccountRegistration/ValidateToken', data));
  }

  validatePasswordResetToken(token: string) {
    return this.postCore('user/ValidatePasswordToken', { token });
  }

  resetPassword(model) {
    return this.postCore('user/ResetPassword', model);
  }

  hasPassword() {
    return this.http.get<boolean>('user/HasPassword');
  }

  getStripeId() {
    return this.http.get<string>('user/GetStripeId');
  }

  changePassword(model) {
    return this.postCore('user/ChangePassword', model);
  }
  /** Step 1 of changing the login email: emails a verification code to the new address. */
  requestEmailChange(newEmail: string): Observable<void> {
    return this.http.post<void>('user/email/change-request', { newEmail });
  }

  /** Step 2: verifies the code; the server then updates B2C, the database, Stripe and the CRM. */
  confirmEmailChange(code: string): Observable<{ email: string }> {
    return this.http.post<{ email: string }>('user/email/confirm', { code });
  }

  /**
   * After the login email changes the cached ID token still carries the old `email` claim.
   * Force a token refresh (no sign-out) so the app picks up the new address.
   * Resolves true when the refreshed ID token shows the new email; false means the caller
   * should ask the user to sign out and back in.
   */
  async refreshIdentityAfterEmailChange(expectedEmail: string): Promise<boolean> {
    const account = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0];
    if (!account) {
      return false;
    }

    const showsNewEmail = () => this.userName?.toLowerCase() === expectedEmail.toLowerCase();

    try {
      const result = await this.authService.instance.acquireTokenSilent({
        account,
        scopes: this.authApiScopes,
        forceRefresh: true,
      });
      if (result?.account) {
        this.authService.instance.setActiveAccount(result.account);
      }
      if (showsNewEmail()) {
        return true;
      }
    } catch (e) {
      console.warn('Silent token refresh after email change failed; trying ssoSilent.', e);
    }

    try {
      // The B2C session cookie is still valid, so a hidden-iframe request re-issues the ID token.
      const result = await this.authService.instance.ssoSilent({
        account,
        authority: this.authAuthority,
        scopes: ['openid', 'profile', ...this.authApiScopes],
      });
      if (result?.account) {
        this.authService.instance.setActiveAccount(result.account);
      }
      return showsNewEmail();
    } catch (e) {
      console.warn('ssoSilent after email change failed.', e);
      return false;
    }
  }

  /**
   * Logout, Logoff, Sign Out
   */
  public signout(): void {
    
    this.listingFavorites.next(null);

    this.clearActiveOrg();

    this.externalOrganizationCodes.next(null);

    this.clientStorage.clear();
    this.clearCachedUserSettings();
    this._profile = undefined;

    this.isLoggedInChangeRaise(false);
  }

  sendPasswordLink(emailAddress: string) {
    return withUnwrappingRethrow(this.http.post('User/sendPasswordLink', { emailAddress }));
  }

  public search(target: string) {
    return this.http.post<NonAdminAccountViewModel[]>('useraccount/search', `"${target}"`);
  }

  /*
   * Determines if a users profile is complete or not.
   */
  isProfileComplete(profile: UserProfile, parts?: string[]) {
    if (!parts) {
      return (
        this.hasValue(profile.firstName) &&
        this.hasValue(profile.lastName) &&
        this.hasValue(profile.phone) &&
        this.hasValue(profile.dateOfBirth) &&
        this.hasValue(profile.street) &&
        this.hasValue(profile.city) &&
        this.hasValue(profile.state) &&
        this.hasValue(profile.zip)
      );
    }

    const match = parts.find((p) => this.hasValue(profile[p]) === false);
    return match === undefined;
  }

  // tslint:disable-next-line:no-any
  private hasValue(val: any) {
    return val !== undefined && val !== null && val.toString() !== '';
  }

  public updateProfile(profile: UserProfile) {
    return withUnwrappingRethrow(this.http.put('UserProfile', profile));
  }

  public postProfile(profile: UserProfile) {
    return withUnwrappingRethrow(this.http.post<UserProfile>('userprofile', profile));
  }

  private postCore(resource: string, body) {
    return withUnwrappingRethrow(this.http.post(resource, body));
  }

  /**
   * Formats the date to a string according to a user's user profile
   *   (not really, this is a placeholder...the format is: MM/dd/yyyy for the foreseeable future)
   * @param date
   */
  public formatDate(date: Date): string {
    // TODO: Actually implement the conversion using a user profile setting or config.  But for now, use the default.
    return this.datePipe.transform(date, this.userDateFormat);
  }

  private _userDateFormat: string = 'MM/dd/yyyy';

  public get userDateFormat(): string {
    return this._userDateFormat;
  }

  /**
   * Gets a Signup Configuration by id
   */
  public getSignupConfiguration(id: string) {
    const c = this.signupConfigurations.find((x) => x.id === id);

    if (!c) {
      console.warn(`Signup Configuration Id: ${id} does not exist.`);
    }

    return c;
  }

  /**
   * Adds a new email to OA Mailing List
   */
  public addEmailToEmailList(model: AddEmailMailingListRequest) {
    if (!model) {
      throw Error('Model is null or empty');
    }

    const url = 'mkt/contact';

    return this.http.post(url, model);
  }

  public setActiveOrg(exOrgCode: string) {
    
    if (this._activatableExternalOrgCodes.includes(exOrgCode)) {
      this.storage.setSessionItem('activeOrg', exOrgCode);
      this.activeOrg.next(exOrgCode);
    }
  }

  public clearActiveOrg() {
    this.storage.removeItem('activeOrg', StorageType.Session);
    this.activeOrg.next(null);
  }

  /**
   * Gets a user's digital signature.
   * Note: this is a feature only used by OA Admins
   * @returns
   */
  public getDigitalSignature() {
    return this.http.get<DigitalSignatureViewModel>('User/sig');
  }

  /**
   * Refreshes User Listing Favorites
   */
  public refreshListingFavorites() {
    
    if (this.isLoggedIn) {
      this.http.get<UserListingFavoriteViewModel>('User/favorite/listings').subscribe(
        (data) => {
    
          this.listingFavorites.next(data);
        },
        (err) => {
          console.error(err);
        }
      );
    } else {
      this.listingFavorites.next(null);
    }
  }

  /**
   * Checks against the favorites in the listingFavorites Behavior subject.  This method DOES NOT make the API call (see refreshListingFavorites)
   */
  public isListingFavorite(listingId: number) {
    if (this.listingFavorites.value) {
      if (this.listingFavorites.value.favorites) {
        if (this.listingFavorites.value.favorites.find((x) => x.listingId === listingId)) return true;
      }
    }

    return false;
  }

  /**
   * Gets the user booking count (future)
   */
  public getUserBookingCount(): Observable<number> {
    if (this.isLoggedIn && this.userId) {
      return this.http.get<number>('User/booking/count');
    } else {
      return of();
    }
  }

  /**
   * Sends the user's position to Outdoor Access
   * api/User/position
   * @param position
   */
  sendPosition(position: any) {
    let json = JSON.stringify(position);
    console.log(json);

    let o = JSON.parse(json);

    return this.http.post('User/position', o);
  }

  /**
   * Determines if the user perferred activity can be shown by evaluating application state
   * and user setting
   */
  public showUserPreferredActivityDialog() {
    
    let userSettings = this.userSettings.value;

    let b = false;

    if (!userSettings || !userSettings.dialogConfigurations) {
      b = true;
    } else {
      if (userSettings.userPreferredActivities == null || userSettings.userPreferredActivities.length === 0) {
        var preferredActivityDialogConfig = userSettings.dialogConfigurations.find((d) => d.name === 'dlg-preferred-activity');

        if (preferredActivityDialogConfig == null || preferredActivityDialogConfig.canShow) {
          if (preferredActivityDialogConfig == null) {
            b = true;
          } else {
            // Make sure we give the user some time in between nags
            if (preferredActivityDialogConfig.lastShown) {
              const amountOfDaysInBetweenNagDialogs = 7;

              if (!this.dateHelper.isLessThanDaysFromNow(amountOfDaysInBetweenNagDialogs, preferredActivityDialogConfig.lastShown)) {
                b = true;
              }
            } else {
              b = true;
            }
          }
        }
      }
    }

    return b;
  }

  
  /**
   * Gets the user profile and background check status and emits signInData value
   */
  public getSignInData() {
    let subs: any[] = [this.getProfile(), this.backgroundCheckService.getStatus()];

    forkJoin(subs)
      .pipe(
        map((results: any[]) => {
          return {
            userProfile: results[0] as UserProfileViewModel,
            backgroundCheckStatus: results[1],
          } as SigninData;
        })
      )
      .subscribe((result) => {

        this.signInData.next(result);
      });
  }
}

function withUnwrappingRethrow<T>(observable: Observable<T>): Observable<unknown> {
  return observable.pipe(
    catchError<T, any>((error) => {
      return observableThrowError(error && typeof error.json === 'function' ? error.json() : error);
    })
  );
}

function isTrueString(value?: string) {
  return value !== undefined && value.toLowerCase() === 'true';
}
